import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    MenuItem,
    TextField,
    Typography,
    useMediaQuery,
} from "@material-ui/core"
import { useTheme } from "@material-ui/core/styles"
import { Autocomplete } from "@material-ui/lab"
import React, { useEffect, useState } from "react"
import type {
    DadosNovoEstabelecimento,
    EstabelecimentoCriado,
    NovoEstabelecimentoPayload,
    OpcaoNovoEstabelecimento,
} from "../../../services/estabelecimentosAplicativo"
import { obterDadosNovoEstabelecimento } from "../../../services/estabelecimentosAplicativo"

const FORM_INICIAL: NovoEstabelecimentoPayload = {
    nome: "",
    nomeAlternativo: "",
    cnpj: "",
    telefone: "",
    categoria: "",
    endereco: "",
    numero: "",
    bairro: "",
    cep: "",
    idCidade: null,
    urlInstagram: "",
    urlFacebook: "",
    urlIfood: "",
    nomeResponsavel: "",
    celularResponsavel: "",
    emailResponsavel: "",
    perfil: "ADMINISTRADOR",
}

type CampoTexto = Exclude<keyof NovoEstabelecimentoPayload, "idCidade">

const OBRIGATORIOS: { campo: keyof NovoEstabelecimentoPayload; label: string }[] = [
    { campo: "nome", label: "Nome do estabelecimento" },
    { campo: "cnpj", label: "CNPJ/CPF" },
    { campo: "categoria", label: "Categoria" },
    { campo: "endereco", label: "Logradouro" },
    { campo: "numero", label: "Número" },
    { campo: "bairro", label: "Bairro" },
    { campo: "cep", label: "CEP" },
    { campo: "idCidade", label: "Cidade" },
    { campo: "nomeResponsavel", label: "Nome do responsável" },
    { campo: "celularResponsavel", label: "Celular do responsável" },
    { campo: "emailResponsavel", label: "E-mail do responsável" },
    { campo: "perfil", label: "Perfil" },
]

type Props = {
    aberto: boolean
    onFechar: () => void
    criar: (payload: NovoEstabelecimentoPayload) => Promise<EstabelecimentoCriado>
    onCriado: (estab: EstabelecimentoCriado) => void
    onErro: (mensagem: string) => void
}

export function DialogNovoEstabelecimento({ aberto, onFechar, criar, onCriado, onErro }: Props) {
    const theme = useTheme()
    const telaPequena = useMediaQuery(theme.breakpoints.down("sm"))
    const [dadosApoio, setDadosApoio] = useState<DadosNovoEstabelecimento | null>(null)
    const [carregando, setCarregando] = useState(false)
    const [salvando, setSalvando] = useState(false)
    const [form, setForm] = useState<NovoEstabelecimentoPayload>(FORM_INICIAL)
    const [erros, setErros] = useState<Partial<Record<keyof NovoEstabelecimentoPayload, boolean>>>(
        {}
    )

    useEffect(() => {
        if (!aberto) return
        setForm(FORM_INICIAL)
        setErros({})
        if (dadosApoio) return
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setCarregando(true)
        obterDadosNovoEstabelecimento(autorizacao)
            .then(resposta => {
                if ("retornoErro" in resposta) {
                    onErro(resposta.mensagem || "Erro ao carregar dados do cadastro")
                    onFechar()
                } else {
                    setDadosApoio(resposta)
                }
            })
            .finally(() => setCarregando(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aberto])

    const setCampo = (campo: CampoTexto) => (e: React.ChangeEvent<{ value: unknown }>) => {
        const valor = String(e.target.value)
        setForm(f => ({ ...f, [campo]: valor }))
        setErros(er => ({ ...er, [campo]: false }))
    }

    const cidadeSelecionada = dadosApoio?.cidades.find(c => c.id === form.idCidade) ?? null

    const handleSalvar = async () => {
        const faltando = OBRIGATORIOS.filter(({ campo }) => {
            const v = form[campo]
            return v === null || String(v).trim() === ""
        })
        if (faltando.length > 0) {
            setErros(Object.fromEntries(faltando.map(f => [f.campo, true])))
            onErro("Preencha: " + faltando.map(f => f.label).join(", "))
            return
        }
        setSalvando(true)
        try {
            const criado = await criar(form)
            onCriado(criado)
            onFechar()
        } catch (err) {
            onErro((err as Error).message || "Erro ao cadastrar estabelecimento")
        } finally {
            setSalvando(false)
        }
    }

    const bloqueado = !!dadosApoio && !dadosApoio.permitido

    const campo = (
        nome: CampoTexto,
        label: string,
        props: Partial<React.ComponentProps<typeof TextField>> = {}
    ) => (
        <TextField
            label={label}
            variant="outlined"
            size="small"
            fullWidth
            value={form[nome]}
            onChange={setCampo(nome)}
            error={!!erros[nome]}
            required={OBRIGATORIOS.some(o => o.campo === nome)}
            disabled={bloqueado || salvando}
            {...props}
        />
    )

    return (
        <Dialog
            open={aberto}
            onClose={salvando ? undefined : onFechar}
            fullWidth
            maxWidth="md"
            fullScreen={telaPequena}
        >
            <DialogTitle>Novo estabelecimento</DialogTitle>
            <DialogContent dividers>
                {carregando || !dadosApoio ? (
                    <Typography style={{ color: "#7f8c8d" }}>Carregando...</Typography>
                ) : (
                    <>
                        <Box mb={2}>
                            <Typography variant="body2" style={{ color: "#555" }}>
                                A loja será criada já vinculada ao aplicativo{" "}
                                <strong>{dadosApoio.nomeAplicativo}</strong>, tendo{" "}
                                <strong>{dadosApoio.rede}</strong> como rede. Os usuários da rede
                                ganham acesso à nova loja.
                            </Typography>
                            {bloqueado && (
                                <Typography
                                    variant="body2"
                                    style={{ color: "var(--cor-erro)", marginTop: 8, fontWeight: 500 }}
                                >
                                    {dadosApoio.motivoBloqueio}
                                </Typography>
                            )}
                        </Box>

                        <Typography variant="subtitle1" style={{ fontWeight: 600 }} gutterBottom>
                            Estabelecimento
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                {campo("nome", "Nome do estabelecimento")}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {campo("nomeAlternativo", "Nome alternativo")}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {campo("cnpj", "CNPJ/CPF", {
                                    helperText: "Informe 0 se ainda não tiver",
                                })}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {campo("telefone", "Telefone da loja")}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {campo("categoria", "Categoria", {
                                    select: true,
                                    children: dadosApoio.categorias.map(c => (
                                        <MenuItem key={c} value={c}>
                                            {c}
                                        </MenuItem>
                                    )),
                                })}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {campo("endereco", "Logradouro")}
                            </Grid>
                            <Grid item xs={6} md={2}>
                                {campo("numero", "Número")}
                            </Grid>
                            <Grid item xs={6} md={4}>
                                {campo("bairro", "Bairro")}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {campo("cep", "CEP", { inputProps: { maxLength: 9 } })}
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Autocomplete
                                    options={dadosApoio.cidades}
                                    getOptionLabel={(c: OpcaoNovoEstabelecimento) => c.nome}
                                    getOptionSelected={(a, b) => a.id === b.id}
                                    value={cidadeSelecionada}
                                    onChange={(_e, nova: OpcaoNovoEstabelecimento | null) => {
                                        setForm(f => ({ ...f, idCidade: nova ? nova.id : null }))
                                        setErros(er => ({ ...er, idCidade: false }))
                                    }}
                                    disabled={bloqueado || salvando}
                                    noOptionsText="Nenhuma cidade encontrada"
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label="Cidade"
                                            variant="outlined"
                                            size="small"
                                            required
                                            error={!!erros.idCidade}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {campo("urlInstagram", "Instagram")}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {campo("urlFacebook", "Facebook")}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {campo("urlIfood", "iFood")}
                            </Grid>
                        </Grid>

                        <Typography
                            variant="subtitle1"
                            style={{ fontWeight: 600, marginTop: 24 }}
                            gutterBottom
                        >
                            Responsável
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                {campo("nomeResponsavel", "Nome")}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {campo("celularResponsavel", "Celular (com DDD)", {
                                    helperText: "Será a senha inicial de acesso",
                                })}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {campo("emailResponsavel", "E-mail (login)", { type: "email" })}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {campo("perfil", "Perfil", {
                                    select: true,
                                    children: dadosApoio.perfis.map(p => (
                                        <MenuItem key={p.valor} value={p.valor}>
                                            {p.descricao}
                                        </MenuItem>
                                    )),
                                })}
                            </Grid>
                        </Grid>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onFechar} disabled={salvando}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSalvar}
                    color="primary"
                    variant="contained"
                    disabled={carregando || !dadosApoio || bloqueado || salvando}
                >
                    {salvando ? "Cadastrando..." : "Cadastrar estabelecimento"}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
